import React from "react";

import { faChevronDown } from "@fortawesome/pro-light-svg-icons";
import { twJoin } from "tailwind-merge";

import { FixedPositionedDropdown } from "src/base-components/FixedPositionedDropDown";
import { Pill } from "src/base-components/Pill";
import { DropdownItem } from "src/design-system/Dropdown";
import { Tooltip } from "src/design-system/Tooltip";
import {
  ColumnType,
  MIN_COLUMNS_FOR_GROUPING_KEY_WITH_ENTITY,
  MIN_COLUMNS_FOR_GROUPING_KEY_WITHOUT_ENTITY,
} from "src/featureCatalogue/Queries/Create/helpers";

const options = [
  { key: "entity", value: "Entity" },
  { key: "feature", value: "Feature" },
  { key: "grouping_key", value: "Grouping by" },
];

export const ColumnTypeSelector: React.FC<{
  columnName: string;
  currentColumnType: ColumnType;
  columnTypes?: Record<string, ColumnType>;
  onColumnTypeChange?: (column: string, type: ColumnType) => void;
}> = ({ columnName, currentColumnType, columnTypes, onColumnTypeChange }) => {
  const hasEntity = Object.values(columnTypes || {}).some(
    (type) => type === "entity",
  );
  const totalColumns = Object.keys(columnTypes || {}).length;
  const canGroupBy =
    totalColumns >=
    (hasEntity
      ? MIN_COLUMNS_FOR_GROUPING_KEY_WITH_ENTITY
      : MIN_COLUMNS_FOR_GROUPING_KEY_WITHOUT_ENTITY);

  const currentOption = options.filter((opt) => {
    // Single column: only feature is allowed
    if (totalColumns <= 1 && opt.key !== "feature") return false;
    // We filter out the entity when it's a global feature
    if (opt.key === "entity" && !hasEntity) return false;

    if (opt.key === "grouping_key" && !canGroupBy) return false;
    return true;
  });

  const selectedOption = options.find((opt) => opt.key === currentColumnType);

  const handleSelect = (newType: string) => {
    const newColumnType = newType as ColumnType;

    if (newColumnType === "entity" || newColumnType === "grouping_key") {
      const existingColumn = Object.entries(columnTypes || {}).find(
        ([col, type]) => col !== columnName && type === newColumnType,
      );

      if (existingColumn) {
        const [existingColumnName] = existingColumn;
        onColumnTypeChange?.(existingColumnName, "feature");
      }
    }
    onColumnTypeChange?.(columnName, newColumnType);
  };

  const isDisabled = hasEntity && currentColumnType === "entity";

  return (
    <FixedPositionedDropdown
      buttonClassName="!outline-none !ring-0 !border-0 focus:!outline-none focus:!ring-0 focus:!border-0 focus-visible:!outline-none"
      buttonDataLoc="view-type-button-dropdown"
      dataLoc="view-type-dropdown"
      disabled={isDisabled}
      elements={currentOption}
      renderButtonValue={() => {
        const selectedOptionValue = selectedOption?.value;
        const tooltipTitle =
          selectedOptionValue === "Entity"
            ? "At least one Column must be set as an Entity. To change the current Entity Column, first assign another Column as the Entity"
            : `${selectedOptionValue} column`;
        return (
          <Tooltip title={tooltipTitle} asChild>
            <div
              className={twJoin(
                "flex w-20 max-w-20 justify-between rounded border border-gray-200 bg-gray-200 px-1",
                isDisabled ? "cursor-not-allowed" : "cursor-pointer",
              )}
            >
              <Pill.Text>{selectedOptionValue}</Pill.Text>
              <Pill.Icon color="gray" icon={faChevronDown} />
            </div>
          </Tooltip>
        );
      }}
      renderValue={({ value, selected }) => (
        <DropdownItem
          data-loc={`view-by-${value.toLowerCase().replace(/\s+/g, "-")}`}
          data-selected={selected || undefined}
        >
          <span>{value}</span>
        </DropdownItem>
      )}
      selected={currentColumnType}
      onSelect={handleSelect}
    />
  );
};
